import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import StoryRow from '../components/StoryRow';
import ContinueReading from '../components/ContinueReading';

const GENRE_ICONS: Record<string, string> = {
  Action: '💥', Adventure: '⚔️', Comedy: '😄', Crime: '🔫', 'Dark Fantasy': '🌑',
  Drama: '🎭', Dystopian: '🏚️', Erotica: '🔥', Fanfiction: '✨', Fantasy: '🏰',
  Historical: '📜', Horror: '👻', 'LGBTQ+': '🏳️‍🌈', LitRPG: '🎮', Mystery: '🔍',
  'New Adult': '🎓', Paranormal: '👁️', Poetry: '🪶', 'Progression Fantasy': '📈',
  Romance: '💕', Romantasy: '💜', 'Sci-Fi': '🚀', 'Short Story': '📝',
  'Slice of Life': '🌿', Thriller: '⚡', 'Urban Fantasy': '🌃', Werewolf: '🐺',
  Western: '🤠', 'Young Adult': '📱',
};

interface HomePageProps {
  onNavigate: (page: string, data?: any) => void;
  user?: any;
}

export default function HomePage({ onNavigate, user }: HomePageProps) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.stories.homepage()
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleStoryClick = (story: any) => onNavigate('story', { id: story.id, slug: story.slug });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <section className="text-center py-16 mb-8">
        <h1 className="text-5xl md:text-6xl font-extrabold mb-4">
          <span className="bg-gradient-to-r from-ink-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            Every Story Deserves
          </span>
          <br />
          <span className="text-white">To Be Discovered</span>
        </h1>
        <p className="text-gray-400 text-lg max-w-xl mx-auto mb-8">
          The platform where new voices shine. Read, write, and discover stories that move you.
        </p>
        <div className="flex gap-4 justify-center">
          <button
            onClick={() => onNavigate('discover')}
            className="px-6 py-3 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors"
          >
            Start Reading
          </button>
          <button
            onClick={() => onNavigate('dashboard')}
            className="px-6 py-3 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-xl font-semibold transition-colors"
          >
            Start Writing
          </button>
        </div>
      </section>

      <ContinueReading onNavigate={onNavigate} user={user} />

      {data?.featured && data.featured.length > 0 && (
        <section className="mb-12">
          <div
            className="relative bg-gradient-to-r from-ink-900 to-purple-900 rounded-2xl p-8 md:p-12 cursor-pointer overflow-hidden"
            onClick={() => handleStoryClick(data.featured[0])}
          >
            <div className="absolute inset-0 bg-black/20" />
            <div className="relative z-10 max-w-2xl">
              <span className="text-ink-300 text-sm font-semibold uppercase tracking-wider">Featured Story</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-3">{data.featured[0].title}</h2>
              <p className="text-gray-300 mb-2">by {data.featured[0].penName?.penName || data.featured[0].author?.username}</p>
              <p className="text-gray-400 line-clamp-2">{data.featured[0].synopsis}</p>
              <div className="flex gap-3 mt-4">
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm">{data.featured[0].genre}</span>
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm">
                  {data.featured[0]._count?.chapters} chapters
                </span>
              </div>
            </div>
          </div>
        </section>
      )}

      <StoryRow title="Rising Stars" stories={data?.rising || []} onStoryClick={handleStoryClick} onTagClick={(tag) => onNavigate('discover', { tag })} badge="⭐" />
      <StoryRow title="New Releases" stories={data?.newReleases || []} onStoryClick={handleStoryClick} onTagClick={(tag) => onNavigate('discover', { tag })} />
      <StoryRow title="Recently Updated" stories={data?.recentlyUpdated || []} onStoryClick={handleStoryClick} onTagClick={(tag) => onNavigate('discover', { tag })} />
      <StoryRow title="Popular This Week" stories={data?.trending || []} onStoryClick={handleStoryClick} onTagClick={(tag) => onNavigate('discover', { tag })} badge="🔥" />
      <StoryRow title="Staff Picks" stories={data?.staffPicks || []} onStoryClick={handleStoryClick} onTagClick={(tag) => onNavigate('discover', { tag })} badge="CURATED" />

      {data?.genres && data.genres.length > 0 && (
        <section className="mb-10">
          <h2 className="text-xl font-bold mb-4">Browse by Genre</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {data.genres.map((g: any) => (
              <button
                key={g.genre}
                onClick={() => onNavigate('discover', { genre: g.genre })}
                className="p-4 bg-gray-900 border border-gray-800 rounded-xl hover:border-ink-500/50 transition-all text-center group"
              >
                <span className="text-2xl block mb-1">{GENRE_ICONS[g.genre] || '📖'}</span>
                <span className="font-medium text-sm group-hover:text-ink-400 transition-colors">{g.genre}</span>
                <span className="block text-xs text-gray-500 mt-1">{g._count.id} stories</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="mb-10">
        <h2 className="text-xl font-bold mb-4">Popular Tags</h2>
        <div className="flex flex-wrap gap-2">
          {['Werewolf', 'Alpha', 'Vampire', 'Billionaire', 'Enemies to Lovers', 'Slow Burn', 'Second Chance', 'Fated Mates', 'Reverse Harem', 'Mafia', 'CEO', 'Dark Romance', 'Shifter', 'Rejected Mate', 'Forbidden Love'].map(tag => (
            <button
              key={tag}
              onClick={() => onNavigate('discover', { tag })}
              className="px-4 py-2 bg-gray-900 border border-gray-800 rounded-full text-sm font-medium hover:border-ink-500/50 hover:text-ink-400 transition-all"
            >
              {tag}
            </button>
          ))}
        </div>
      </section>

      {(!data?.rising?.length && !data?.trending?.length && !data?.recentlyUpdated?.length) && (
        <section className="text-center py-16 bg-gray-900/50 rounded-2xl border border-gray-800">
          <span className="text-5xl mb-4 block">✍️</span>
          <h2 className="text-2xl font-bold mb-2">The stage is set</h2>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            ScribeHaven is brand new and ready for its first stories. Be among the first authors to share your work.
          </p>
          <button
            onClick={() => onNavigate('dashboard')}
            className="px-6 py-3 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors"
          >
            Write Your First Story
          </button>
        </section>
      )}
    </div>
  );
}
